import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectFilteredEdgeNodes } from '../map/slice/mapSelectors';
import { generateColor } from '../utils/colors';
import { fromLonLat } from 'ol/proj';
import {
  selectRiseChains,
  selectShowRiseChains,
} from '../plotter/plotterSelectors';
import { RiseChain } from '@/types';
import { MapTooltip } from './MapTooltip';
import { useMapLayer } from '../hooks/useMapLayer';
import { FeatureLike } from '../utils/geoJsonUtils';
import { useMapContext, MapEventListener } from './context/MapContext';

const getPathStyle = () => [
  {
    filter: ['<=', ['get', 'missingInterconnect'], 0],
    style: {
      'stroke-width': ['get', 'width'],
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      'stroke-opacity': 0.8,
    },
  },
  {
    filter: ['>', ['get', 'missingInterconnect'], 0],
    style: {
      'stroke-width': ['get', 'width'],
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      'stroke-opacity': 0.8,
      'stroke-line-dash': [15, 20, 5, 20],
      'stroke-line-dash-offset': 0,
    },
  },
];

export type MapRisePathsProps = {
  zIndex: number;
};

export const MapRisePaths: React.FC<MapRisePathsProps> = ({ zIndex }) => {
  const { map, registerHoverListener, unregisterHoverListener } =
    useMapContext();
  const nodes = useSelector(selectFilteredEdgeNodes);
  const showRisePaths = useSelector(selectShowRiseChains);
  const riseChains = useSelector(selectRiseChains);

  const [tooltipRiseChain, setTooltipTransport] = useState<RiseChain | null>(
    null,
  );

  const hoverListener: MapEventListener = useCallback(
    (feature) => {
      if (
        feature?.getProperties()?.key ===
        `rise-path-${tooltipRiseChain?.chain_id}`
      )
        return;
      if (!feature?.getProperties()?.key?.startsWith('rise-path-')) {
        setTooltipTransport(null);
        return;
      }
      const chainId = feature?.getProperties()?.chainId;
      const chain = riseChains.find(
        (t) => t.chain_id?.toString() === chainId?.toString(),
      );
      if (chain) {
        setTooltipTransport(chain);
      }
    },
    [riseChains],
  );

  const _features = useMemo<FeatureLike[]>(
    () =>
      riseChains
        .filter((chain) => chain.visible)
        .flatMap((chain) => {
          return (
            chain.operation_links?.map((operation) => {
              const pathNodes = operation.operation_path
                .map((nodeId) => nodes[nodeId])
                .filter(Boolean);

              return {
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: pathNodes.map((node) =>
                    fromLonLat([Number(node.lng), Number(node.lat)]),
                  ),
                },
                properties: {
                  id: operation.operation_id,
                  chainId: chain.chain_id,
                  key: `rise-path-${chain.chain_id}-${operation.operation_id}`,
                  width: 20,
                  color: generateColor(chain.chain_id),
                  missingInterconnect: 0,
                },
              };
            }) ?? []
          );
        }),
    [riseChains, nodes],
  );

  const features = useMemo(() => {
    if (!showRisePaths) return [];
    return _features;
  }, [showRisePaths, _features]);

  const style = useMemo(() => getPathStyle(), []);

  useMapLayer(features, { zIndex, style });

  useEffect(() => {
    if (!map) return;
    registerHoverListener(hoverListener);
    return () => {
      unregisterHoverListener(hoverListener);
    };
  }, [map, hoverListener]);

  return (
    <>
      <MapTooltip visible={Boolean(tooltipRiseChain)}>
        <div className="bg-dark p-2 px-3 border-dark text-light rounded">
          <p className="fw-bold">Chain {tooltipRiseChain?.chain_id}</p>
          {`Rise Chain #${tooltipRiseChain?.chain_id}: ${tooltipRiseChain?.chain_delivered_dth} Dth`}
        </div>
      </MapTooltip>
    </>
  );
};
